import React, { useEffect, useState } from "react";
import {
  Home,
  BarChart2,
  Database,
  CreditCard,
  Settings,
  Bell,
  HelpCircle,
  Power,
  User,
  UserCog,
  Users,
  KeyRound,
  Scale,
  ChevronDown,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { canManageOrgUsers } from "../../utils/roles";
import "./layout.scss";

const getSubdomain = (hostname: string): string => {
  const parts = hostname.split(".");
  if (parts.length > 3 || (parts.length === 3 && parts[1] === "business")) {
    return parts[0];
  }
  return "";
};

interface SidebarProps {
  className?: string;
}

interface UserInfo {
  user_id: string;
  email: string;
  name: string;
  given_name: string;
  family_name: string;
  preferred_username: string;
  org_id: string;
  org_domain: string;
  roles: string[];
  permissions: string[];
  email_verified: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ className = "" }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const location = useLocation();
  const inSettings =
    location.pathname.startsWith("/account") || location.pathname.startsWith("/settings");
  const [settingsOpen, setSettingsOpen] = useState(inSettings);

  useEffect(() => {
    const storedUser = localStorage.getItem("user_info");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing user info:", error);
      }
    }
  }, []);

  const handleLogout = () => {
    console.log("🚪 Initiating logout...");

    // Get current subdomain BEFORE clearing data
    const currentHostname = window.location.hostname;
    const currentSubdomain = getSubdomain(currentHostname);

    if (currentSubdomain) {
      console.log("📍 Logging out from subdomain:", currentSubdomain);

      // Store subdomain for post-logout redirect
      localStorage.setItem("logout_return_subdomain", currentSubdomain);

      // Store in cookie as backup (survives Zitadel redirect)
      document.cookie = `logout_subdomain=${currentSubdomain}; path=/; domain=.business.chargingadda.com; max-age=600; secure; SameSite=Lax`;
    }

    // Get ID token for Zitadel logout
    const idToken = localStorage.getItem("id_token");

    // Clear ALL localStorage
    localStorage.clear();

    // Build Zitadel logout URL
    const zitadelDomain = "https://idp.cms.chargingadda.com";
    const postLogoutRedirectUri = "https://business.chargingadda.com"; // Landing page

    const logoutUrl =
      `${zitadelDomain}/oidc/v1/end_session?` +
      `id_token_hint=${idToken}&` +
      `post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;

    console.log("🔐 Redirecting to Zitadel logout...");
    console.log(
      "📍 Will return to landing page, then redirect to:",
      currentSubdomain,
    );

    // Redirect to Zitadel logout
    window.location.href = logoutUrl;
  };

  // const handleLogout = () => {
  //   const accessToken = localStorage.getItem("id_token");
  //   const zitadelDomain = "https://idp.cms.chargingadda.com";
  //   const postLogoutRedirectUri = "https://business.chargingadda.com";

  //   localStorage.clear();

  //   window.location.href =
  //     `${zitadelDomain}/oidc/v1/end_session?` +
  //     `id_token_hint=${accessToken}&` +
  //     `post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
  // };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const firstLetter = user?.name?.charAt(0).toUpperCase() || "U";

  return (
    <aside className={`sidebar ${className}`}>
      <div className="logo">
        <span className="logo-circle">{firstLetter}</span>
        <h2>{user ? user.name : "ChargingAdda"}</h2>
      </div>

      <nav className="menu">
        <ul>
          <li className={isActive("/dashboard") ? "active" : ""}>
            <a href="/dashboard">
              <Home size={18} /> Dashboard
            </a>
          </li>
          {/* <li className={isActive("/analytics") ? "active" : ""}>
            <a href="/analytics">
              <BarChart2 size={18} /> Analytics
            </a>
          </li> */}
          <li className={isActive("/stations") ? "active" : ""}>
            <a href="/stations">
              <Database size={18} /> Stations
            </a>
          </li>

          <li className={isActive("/settlements") ? "active" : ""}>
            <a href="/settlements">
              <Scale size={18} /> Settlements
            </a>
          </li>
          <li className={isActive("/transactions") ? "active" : ""}>
            <a href="/transactions">
              <CreditCard size={18} /> Transactions
            </a>
          </li>
          <li className={isActive("/manageUser") ? "active" : ""}>
            <a href="/manageUser">
              <User size={18} /> Manage User
            </a>
          </li>
          <li className={isActive("/support") ? "active" : ""}>
            <a href="/support">
              <HelpCircle size={18} /> Support
            </a>
          </li>
          <li
            className={`has-sub ${inSettings ? "open-parent" : ""} ${settingsOpen ? "open" : ""}`}
            onClick={() => setSettingsOpen((o) => !o)}
          >
            <span className="sub-toggle">
              <Settings size={18} /> Settings
              <ChevronDown size={16} className="chev" />
            </span>
          </li>
          {settingsOpen && (
            <ul className="submenu">
              <li className={isActive("/account") ? "active" : ""}>
                <a href="/account">
                  <UserCog size={18} /> My Account
                </a>
              </li>
              {canManageOrgUsers() && (
                <li className={isActive("/settings/members") ? "active" : ""}>
                  <a href="/settings/members">
                    <Users size={18} /> Members
                  </a>
                </li>
              )}
              {canManageOrgUsers() && (
                <li className={isActive("/settings/login-methods") ? "active" : ""}>
                  <a href="/settings/login-methods">
                    <KeyRound size={18} /> Sign-in methods
                  </a>
                </li>
              )}
            </ul>
          )}
          {/* <li className={isActive("/settings") ? "active" : ""}>
            <a href="/settings">
              <Settings size={18} /> Settings
            </a>
          </li>
          <li className={isActive("/alerts") ? "active" : ""}>
            <a href="/alerts">
              <Bell size={18} /> Alerts
            </a>
          </li> */}
        </ul>
      </nav>

      <div className="footer">
        <button className="logout" onClick={handleLogout}>
          <Power size={18} /> Logout
        </button>

        <div className="meta">
          <p>Terms of Service | Privacy Policy</p>
          <small>Last sync: 2 min ago</small>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
